import { Swiper, Pagination, Autoplay, EffectFade, Navigation } from 'swiper';
import LazyLoad from './lazy-load';

const checkIfElementIsVisible = require('../helpers/checkIfElementIsVisible');
const resizeFunctions = [];

/**
 * Initialize Slider.
 *
 * @param {HTMLElement} $component
 */
const stopVideo = videos => {
	videos.forEach(video => {
		video.setAttribute('autoplay', false);
		video.removeAttribute('loop');
		video.pause();
		video.currentTime = 0;
	});
};
const startVideoAndSetEventAfterEnd = (videos, activeSlide, swiper) => {
	if (videos.length > 0) {
		activeSlide.dataset.swiperAutoplay = 5000000000;
		videos.forEach(video => {
			// If video is visible, play
			if (checkIfElementIsVisible(video)) {
				video.play();
				video.addEventListener('ended', () => {
					swiper.slideNext();
				});
			}
		});
	}
};
const initSliderForHero = ($component, settings) => {
	const $swiper = $component.querySelector('.swiper-container');
	if (!$swiper) return;
	window.addEventListener('load', () => {
		// stop all videos
		const $allVideos = $swiper.querySelectorAll('video');
		stopVideo($allVideos);

		// add swiper
		const swiper = new Swiper($swiper, settings);

		// fire event when active index change
		swiper.on('activeIndexChange', () => {
			stopVideo($allVideos);

			// Lazyload new active slide
			LazyLoad.init(swiper.slides[swiper.activeIndex], true);

			// setTimeout needed to get activeSlide after dom get updated
			setTimeout(() => {
				const $activeSlide = $swiper.querySelector('.swiper-slide-active');
				const $videos = $activeSlide.querySelectorAll('video');
				startVideoAndSetEventAfterEnd($videos, $activeSlide, swiper);
			});
		});

		// Lazyload first slide of imageswiper
		LazyLoad.init(swiper.slides[swiper.activeIndex], true);

		// fire event before init
		swiper.on('beforeInit', () => {
			// beforeInit active slide is always first slide
			const $activeSlide = $swiper.querySelector('.swiper-slide:first-of-type');
			const $videos = $activeSlide.querySelectorAll('video');
			startVideoAndSetEventAfterEnd($videos, $activeSlide, swiper);
		});

		// init swiper
		swiper.init();
	});
};

/**
 * Initialize Hero Carousel Swiper.
 *
 * @param {HTMLElement} $root container to query for js-hero-carousel-slider
 */
const initHeroCarouselSlider = ($root = document) => {
	$root.querySelectorAll('.js-hero-carousel-slider').forEach($container => {
		const $pagination = $container.querySelector('.swiper-pagination');
		const settings = {
			modules: [Pagination, Autoplay],
			direction: 'horizontal',
			loop: true,
			speed: 3000,
			slidesPerView: 'auto',
			grabCursor: true,
			autoplay: {
				delay: 5000,
				disableOnInteraction: false
			},
			pagination: {
				el: $pagination,
				clickable: true
			},
			init: false
		};
		initSliderForHero($container, settings);
	});
};

/**
 * Initialize Product Carousel.
 *
 * @param {HTMLElement} $root container to query for js-product-carousel
 */
const initProductCarousel = ($root = document) => {
	$root.querySelectorAll('.js-product-carousel').forEach($container => {
		const $thumbswiper = $container.querySelector('.swiper-container.product-carousel-thumbs');
		const $imageswiper = $container.querySelector('.swiper-container.product-carousel-image');
		const $contentswiper = $container.querySelector(
			'.swiper-container.product-carousel-content'
		);

		const thumbswiper = new Swiper($thumbswiper, {
			modules: [Navigation],
			slidesPerView: 1,
			direction: 'horizontal',
			spaceBetween: 4,
			slideToClickedSlide: true,
			grabCursor: true,
			navigation: {
				nextEl: $container.querySelector('.swiper-button-next'),
				prevEl: $container.querySelector('.swiper-button-prev')
			},
			breakpoints: {
				1024: {
					direction: 'vertical'
				}
			}
		});

		const contentswiper = new Swiper($contentswiper, {
			speed: 0,
			direction: 'horizontal',
			slidesPerView: 1,
			allowTouchMove: false,
			autoHeight: window.innerWidth < 769 // Autoheight on mobile
		});

		const imageswiper = new Swiper($imageswiper, {
			modules: [EffectFade],
			speed: 1000,
			direction: 'horizontal',
			slidesPerView: 1,
			allowTouchMove: false,
			effect: 'fade',
			fadeEffect: {
				crossFade: true
			}
		});

		// Set class that takes care of some styling based on having few items
		if (contentswiper.slides.length <= 6) $container.classList.add('has-six-or-less-items');

		// Align thumbs with content once loaded
		setTimeout(() => {
			$container.style.setProperty('--product-carousel-thumb-margin-top', `${$contentswiper.offsetTop}px`);	
		}, 0);

		const $select = $container.querySelector('.js-product-carousel-selectbox select');
		$select.addEventListener('change', () => {
			thumbswiper.slideTo($select.selectedIndex);
		});
		thumbswiper.on('slideChange', () => {
			const newIndex = thumbswiper.realIndex;
			$select.selectedIndex = newIndex;
			imageswiper.slideTo(newIndex);
			contentswiper.slideTo(newIndex);
			// Lazyload new active slide of imageswiper
			LazyLoad.init(imageswiper.slides[imageswiper.activeIndex], true);
		});

		// Lazyload first slide of imageswiper
		LazyLoad.init(imageswiper.slides[imageswiper.activeIndex], true);

		const colorClassPrefix = 'has-bg-';

		const setBackgroundColor = () => {
			// remove all background classes from swiper container
			$container.className.split(' ').forEach(className => {
				if (className.startsWith(colorClassPrefix)) $container.classList.remove(className);
			});

			// get active slide element
			const $slide = contentswiper.slides[contentswiper.activeIndex];

			// check if slide has attribute 'data-content-bg' set
			if ($slide && $slide.dataset.contentBg) {
				// add class to swiper container for bg color
				$container.classList.add(`${colorClassPrefix}${$slide.dataset.contentBg}`);
			}
		};

		contentswiper.on('activeIndexChange', setBackgroundColor);

		setBackgroundColor();
	});
};

/**
 * Initialize Card Overview Slider.
 *
 * @param {HTMLElement} $root container to query for js-card-overview-slider
 */
const initCardOverviewSlider = ($root = document) => {
	$root.querySelectorAll('.js-card-overview-slider').forEach($container => {
		const $swiper = $container.querySelector('.swiper-container');
		const $pagination = $container.querySelector('.swiper-pagination');
		const slidesPerViewGroup = Number($container.getAttribute('data-slides-per-view')) || 5;
		// On tablet, more than 3 doesn't fit, usually due to content like buttons.
		// So on tablet we only use the same slidesPerViewGroup if it is 3 or less
		const tabletSlidesPerViewGroup = slidesPerViewGroup > 3 ? 3 : slidesPerViewGroup;
		// Optional navigation
		const $nextEl = $container.querySelector('.swiper-button-next');
		const $prevEl = $container.querySelector('.swiper-button-prev');
		const hasNavigation = !!$nextEl && !!$prevEl;

		const settings = {
			modules: [Pagination, Autoplay],
			direction: 'horizontal',
			speed: 3000,
			grabCursor: true,
			autoplay: {
				delay: 5000,
				disableOnInteraction: false
			},
			pagination: {
				el: $pagination,
				clickable: true
			},

			spaceBetween: 14,
			slidesPerView: tabletSlidesPerViewGroup,
			slidesPerGroup: tabletSlidesPerViewGroup,
			breakpoints: {
				1024: {
					spaceBetween: 28,
					slidesPerView: slidesPerViewGroup,
					slidesPerGroup: slidesPerViewGroup
				}
			}
		};

		if (hasNavigation) {
			settings.modules.push(Navigation);
			settings.navigation = {
				nextEl: $nextEl,
				prevEl: $prevEl
			};
		}

		const swiper = new Swiper($swiper, settings);

		const enableOrDisableBasedOnBreakpoint = () => {
			if (window.innerWidth < 769) {
				swiper.enabled = false;
				$container.classList.toggle('is-disabled', true);
			} else {
				swiper.enabled = true;
				$container.classList.toggle('is-disabled', false);
			}
		};
		// On resize
		resizeFunctions.push(() => {
			enableOrDisableBasedOnBreakpoint();
		});
		// Initial
		enableOrDisableBasedOnBreakpoint();
	});
};

const init = () => {
	initHeroCarouselSlider();
	initProductCarousel();
	initCardOverviewSlider();
};

export default {
	init,
	onResize: () => {
		resizeFunctions.forEach(func => func());
	}
};
